import { useMutation, useQueryClient } from "@tanstack/react-query";
import fetchToBackend from "@/utils/fetchToBackend";
import {
  contractingActivityKeys,
  contractingKeys,
  dashboardKeys,
  searchProfileKeys,
  statusCountsKeys,
  teamKeys,
} from "@/lib/query-keys";
import { capture } from "@/lib/posthog";
import type { ContractingListResponse } from "@/types/domain";

/**
 * Asigna el responsable de una licitación (o lo quita con `assigned_user_id`
 * null). Es el traspaso entre comerciales, así que además del listado hay que
 * invalidar el historial, donde queda registrado.
 */
export function useAssignContracting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      contracting_id: number;
      assigned_user_id: string | null;
    }) =>
      fetchToBackend("/contractinguser/assign", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    // El avatar de "la lleva X" cambia en el acto: es la señal de que el reparto
    // quedó hecho y se mira mucho en la vista de kanban.
    onMutate: async ({ contracting_id, assigned_user_id }) => {
      await qc.cancelQueries({ queryKey: contractingKeys.lists() });
      const snapshot = qc.getQueriesData<ContractingListResponse>({
        queryKey: contractingKeys.lists(),
      });

      qc.setQueriesData<ContractingListResponse>(
        { queryKey: contractingKeys.lists() },
        (old) =>
          old?.contractings
            ? {
                ...old,
                contractings: old.contractings.map((c) =>
                  c.contracting_id === contracting_id
                    ? {
                        ...c,
                        contractinguser: {
                          contractinguser_status:
                            c.contractinguser?.contractinguser_status ?? 0,
                          assignedUserId: assigned_user_id,
                        },
                      }
                    : c,
                ),
              }
            : old,
      );

      return { snapshot };
    },
    onSuccess: (_data, { contracting_id, assigned_user_id }) => {
      capture(
        assigned_user_id ? "contracting_assigned" : "contracting_unassigned",
        { contracting_id },
      );
    },
    onError: (_err, _vars, context) => {
      context?.snapshot?.forEach(([key, value]) => qc.setQueryData(key, value));
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: contractingKeys.lists() });
      qc.invalidateQueries({ queryKey: dashboardKeys.all });
      qc.invalidateQueries({ queryKey: statusCountsKeys.all });
      qc.invalidateQueries({
        queryKey: [...contractingKeys.details(), vars.contracting_id],
      });
      qc.invalidateQueries({
        queryKey: contractingActivityKeys.timeline(vars.contracting_id),
      });
    },
  });
}

/** Reemplaza los comerciales asignados a una línea de negocio. */
export function useSetSearchProfileAssignees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { searchprofile_id: number; user_ids: string[] }) =>
      fetchToBackend(`/searchprofiles/${vars.searchprofile_id}/assignees`, {
        method: "PUT",
        body: JSON.stringify({ user_ids: vars.user_ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: searchProfileKeys.all });
      // Cambia qué oportunidades ve cada uno, así que el listado ya no vale.
      qc.invalidateQueries({ queryKey: contractingKeys.all });
      qc.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

/** Activa o desactiva a un miembro del equipo. */
export function useSetMemberActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { user_id: string; is_active: boolean }) =>
      fetchToBackend("/organization/members/active", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.all }),
  });
}
